import { useEffect, useRef, useState, useCallback } from "react";
import {
    FiDownload,
    FiLayers,
    FiColumns,
    FiLock,
    FiUnlock,
    FiRefreshCw,
} from "react-icons/fi";
import { analyzeFaceSmile } from "../../utils/smileDesign/dentalAccuracy";

interface Props {
    preview: string | null;
    imageUrl?: string | null;
    openFullscreen: () => void;
    onDownloadBefore?: () => void;
    onDownloadAfter?: () => void;
}

type ViewMode = "split" | "overlay";
type PanelId = "before" | "after";

interface SmileFocus {
    cx: number;
    cy: number;
    nw: number;
    nh: number;
}

interface ViewState {
    zoom: number;
    panX: number;
    panY: number;
}

const imageClass =
    "absolute inset-0 w-full h-full object-contain object-center pointer-events-none select-none";

const FALLBACK_FOCUS: SmileFocus = { cx: 0.5, cy: 0.62, nw: 0.48, nh: 0.3 };
const INITIAL_VIEW: ViewState = { zoom: 1, panX: 0, panY: 0 };
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.18;
const SMILE_VIEW_PAD = 0.42;

function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
}

async function detectSmileFocus(src: string): Promise<SmileFocus | null> {
    try {
        const result = await analyzeFaceSmile(src);
        const box = result.smileRegion?.boundingBox;
        if (!box || box.width < 8 || box.height < 8) return null;

        const iw = result.imageWidth;
        const ih = result.imageHeight;
        const padX = box.width * SMILE_VIEW_PAD;
        const padY = box.height * SMILE_VIEW_PAD;
        const x = Math.max(0, box.x - padX);
        const y = Math.max(0, box.y - padY);
        const right = Math.min(iw, box.x + box.width + padX);
        const bottom = Math.min(ih, box.y + box.height + padY);
        const w = Math.max(1, right - x);
        const h = Math.max(1, bottom - y);

        return {
            cx: (x + w / 2) / iw,
            cy: (y + h / 2) / ih,
            nw: w / iw,
            nh: h / ih,
        };
    } catch {
        return null;
    }
}

function roundZoom(value: number) {
    return Math.round(value * 100) / 100;
}

function useSmileFocus(src: string | null): SmileFocus | null {
    const [focus, setFocus] = useState<SmileFocus | null>(null);

    useEffect(() => {
        if (!src) {
            setFocus(null);
            return;
        }

        let cancelled = false;
        setFocus(null);
        void detectSmileFocus(src).then((next) => {
            if (!cancelled) setFocus(next);
        });

        return () => {
            cancelled = true;
        };
    }, [src]);

    return focus;
}

function SmileZoomPane({
    src,
    label,
    focus,
    view,
    onViewChange,
    emptyText,
    accent = false,
}: {
    src: string | null;
    label: string;
    focus: SmileFocus;
    view: ViewState;
    onViewChange: (next: ViewState) => void;
    emptyText?: string;
    accent?: boolean;
}) {
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const viewRef = useRef(view);
    const onViewChangeRef = useRef(onViewChange);
    const [natural, setNatural] = useState({ w: 0, h: 0 });
    const [loadedSrc, setLoadedSrc] = useState(src);
    const [viewport, setViewport] = useState({ w: 0, h: 0 });
    const dragRef = useRef<{
        pointerId: number;
        startX: number;
        startY: number;
        originX: number;
        originY: number;
    } | null>(null);

    viewRef.current = view;
    onViewChangeRef.current = onViewChange;

    if (src !== loadedSrc) {
        setLoadedSrc(src);
        setNatural({ w: 0, h: 0 });
    }

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;

        const measure = () => {
            setViewport({ w: el.clientWidth, h: el.clientHeight });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [src]);

    useEffect(() => {
        const el = viewportRef.current;
        if (!el || !src) return;

        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const current = viewRef.current;
            const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
            const nextZoom = clamp(roundZoom(current.zoom + delta), MIN_ZOOM, MAX_ZOOM);
            if (nextZoom === current.zoom) return;
            onViewChangeRef.current({ ...current, zoom: nextZoom });
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [src]);

    const smileW = natural.w * focus.nw;
    const smileH = natural.h * focus.nh;
    const fitScale =
        smileW > 0 && smileH > 0 && viewport.w > 0 && viewport.h > 0
            ? Math.min(viewport.w / smileW, viewport.h / smileH) * 0.9
            : 1;
    const scale = fitScale * view.zoom;
    const tx = viewport.w / 2 - natural.w * focus.cx * scale + view.panX;
    const ty = viewport.h / 2 - natural.h * focus.cy * scale + view.panY;

    return (
        <div className="relative flex-1 min-w-0 min-h-[220px] sm:min-h-0 overflow-hidden bg-[#0E0F11]">
            <span
                className={`absolute top-3 left-3 z-[5] bg-[rgba(10,11,13,0.75)] backdrop-blur-sm px-[11px] py-[5px] rounded-full text-xs font-semibold tracking-wide pointer-events-none ${
                    accent
                        ? "border border-[rgba(29,168,198,0.35)] text-[#1DA8C6]"
                        : "border border-white/[0.07]"
                }`}
            >
                {label}
            </span>
            <span className="absolute top-3 right-3 z-[5] bg-[rgba(10,11,13,0.75)] backdrop-blur-sm px-[9px] py-[4px] rounded-full text-[11px] font-semibold tabular-nums text-[#A5A6AC] border border-white/[0.07] pointer-events-none">
                {Math.round(view.zoom * 100)}%
            </span>

            <div
                ref={viewportRef}
                className={`absolute inset-0 overflow-hidden touch-none overscroll-contain ${
                    src ? "cursor-grab active:cursor-grabbing" : ""
                }`}
                onPointerDown={(e) => {
                    if (!src || e.button !== 0) return;
                    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                    dragRef.current = {
                        pointerId: e.pointerId,
                        startX: e.clientX,
                        startY: e.clientY,
                        originX: view.panX,
                        originY: view.panY,
                    };
                }}
                onPointerMove={(e) => {
                    const drag = dragRef.current;
                    if (!drag || drag.pointerId !== e.pointerId) return;
                    onViewChange({
                        ...view,
                        panX: drag.originX + (e.clientX - drag.startX),
                        panY: drag.originY + (e.clientY - drag.startY),
                    });
                }}
                onPointerUp={(e) => {
                    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
                }}
                onPointerCancel={() => {
                    dragRef.current = null;
                }}
                onDoubleClick={() => onViewChange(INITIAL_VIEW)}
            >
                {src ? (
                    <img
                        src={src}
                        alt={label}
                        draggable={false}
                        onLoad={(e) => {
                            const img = e.currentTarget;
                            if (img.naturalWidth && img.naturalHeight) {
                                setNatural({ w: img.naturalWidth, h: img.naturalHeight });
                            }
                        }}
                        className="absolute max-w-none select-none pointer-events-none"
                        style={{
                            width: natural.w || undefined,
                            height: natural.h || undefined,
                            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                            transformOrigin: "0 0",
                            visibility: natural.w ? "visible" : "hidden",
                        }}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                        <div>
                            <p className="text-sm font-semibold">{emptyText ?? "No image"}</p>
                            <p className="text-sm text-[#6B6C72] mt-1">
                                Generated smile design will appear here
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function BeforeAfterPreview({
    preview,
    imageUrl,
    openFullscreen,
    onDownloadBefore,
    onDownloadAfter,
}: Props) {
    const [overlayOpacity, setOverlayOpacity] = useState(50);
    const [viewMode, setViewMode] = useState<ViewMode>("split");
    const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);
    const [zoomLocked, setZoomLocked] = useState(false);
    const [sharedView, setSharedView] = useState<ViewState>(INITIAL_VIEW);
    const [beforeView, setBeforeView] = useState<ViewState>(INITIAL_VIEW);
    const [afterView, setAfterView] = useState<ViewState>(INITIAL_VIEW);
    const lastPanelRef = useRef<PanelId>("before");

    const displayAfterUrl = imageUrl ?? null;
    const activeImageSrc = preview ?? displayAfterUrl ?? null;
    const detectedBefore = useSmileFocus(preview);
    const detectedAfter = useSmileFocus(displayAfterUrl);
    const beforeFocus = detectedBefore ?? FALLBACK_FOCUS;
    const afterFocus = detectedAfter ?? detectedBefore ?? FALLBACK_FOCUS;

    useEffect(() => {
        if (!activeImageSrc) {
            setImageAspectRatio(null);
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            if (cancelled || !img.naturalWidth || !img.naturalHeight) return;
            setImageAspectRatio(img.naturalWidth / img.naturalHeight);
        };
        img.onerror = () => {
            if (!cancelled) setImageAspectRatio(null);
        };
        img.src = activeImageSrc;

        return () => {
            cancelled = true;
        };
    }, [activeImageSrc]);

    useEffect(() => {
        setSharedView(INITIAL_VIEW);
        setBeforeView(INITIAL_VIEW);
        setAfterView(INITIAL_VIEW);
    }, [displayAfterUrl, preview]);

    const applyView = useCallback(
        (panel: PanelId, next: ViewState) => {
            lastPanelRef.current = panel;
            if (zoomLocked) {
                setSharedView(next);
                return;
            }
            if (panel === "before") setBeforeView(next);
            else setAfterView(next);
        },
        [zoomLocked]
    );

    const toggleLock = () => {
        setZoomLocked((prev) => {
            if (!prev) {
                const source =
                    lastPanelRef.current === "after" ? afterView : beforeView;
                setSharedView(source);
            } else {
                setBeforeView(sharedView);
                setAfterView(sharedView);
            }
            return !prev;
        });
    };

    const resetViews = () => {
        setSharedView(INITIAL_VIEW);
        setBeforeView(INITIAL_VIEW);
        setAfterView(INITIAL_VIEW);
    };

    const beforeViewState = zoomLocked ? sharedView : beforeView;
    const afterViewState = zoomLocked ? sharedView : afterView;

    return (
        <div className="w-full flex flex-col bg-[#212226] border border-white/[0.07] rounded-[14px] overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-3 sm:px-5 pt-4 sm:pt-[18px] pb-3 shrink-0 flex-wrap">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold mb-0.5">Live Preview</h3>
                    <p className="text-sm text-[#6B6C72]">
                        {viewMode === "overlay"
                            ? "Fade the designed smile over the original to compare tooth size"
                            : displayAfterUrl
                              ? zoomLocked
                                  ? "Zoom locked — scroll either side to zoom both"
                                  : "Scroll an image to zoom · lock to zoom both together"
                              : "Smile close-up of the original photo"}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    {displayAfterUrl && (
                        <div className="flex items-center rounded-[9px] bg-[#26282C] border border-white/[0.07] p-0.5">
                            <button
                                type="button"
                                onClick={() => setViewMode("split")}
                                title="Side-by-side smile close-up"
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-[7px] text-xs font-semibold transition-colors ${
                                    viewMode === "split"
                                        ? "bg-[rgba(29,168,198,0.18)] text-[#1DA8C6]"
                                        : "text-[#A5A6AC] hover:text-[#F1F0EA]"
                                }`}
                            >
                                <FiColumns className="w-3 h-3" />
                                <span className="hidden sm:inline">Split</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode("overlay")}
                                title="Superimpose overlay"
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-[7px] text-xs font-semibold transition-colors ${
                                    viewMode === "overlay"
                                        ? "bg-[rgba(29,168,198,0.18)] text-[#1DA8C6]"
                                        : "text-[#A5A6AC] hover:text-[#F1F0EA]"
                                }`}
                            >
                                <FiLayers className="w-3 h-3" />
                                <span className="hidden sm:inline">Overlay</span>
                            </button>
                        </div>
                    )}
                    {viewMode === "split" && displayAfterUrl && (
                        <>
                            <button
                                type="button"
                                onClick={toggleLock}
                                title={
                                    zoomLocked
                                        ? "Unlock so each side zooms on its own"
                                        : "Lock zoom so both sides zoom together"
                                }
                                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-[9px] text-sm font-semibold transition-colors ${
                                    zoomLocked
                                        ? "bg-[rgba(29,168,198,0.18)] border border-[rgba(29,168,198,0.35)] text-[#1DA8C6]"
                                        : "bg-[#26282C] border border-white/[0.07] text-[#A5A6AC] hover:border-[#1DA8C6] hover:text-[#1DA8C6]"
                                }`}
                            >
                                {zoomLocked ? (
                                    <FiLock className="w-[13px] h-[13px]" />
                                ) : (
                                    <FiUnlock className="w-[13px] h-[13px]" />
                                )}
                                <span className="hidden sm:inline">
                                    {zoomLocked ? "Locked" : "Lock"}
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={resetViews}
                                title="Reset smile zoom"
                                className="flex items-center gap-1.5 bg-[#26282C] border border-white/[0.07] text-[#A5A6AC] px-2.5 sm:px-3 py-2 rounded-[9px] text-sm font-semibold hover:border-[#1DA8C6] hover:text-[#1DA8C6] transition-colors"
                            >
                                <FiRefreshCw className="w-[13px] h-[13px]" />
                                <span className="hidden sm:inline">Reset</span>
                            </button>
                        </>
                    )}
                    {onDownloadBefore && (
                        <button
                            type="button"
                            onClick={onDownloadBefore}
                            disabled={!preview}
                            title="Download before image"
                            className="flex items-center gap-1.5 bg-[#26282C] border border-white/[0.07] text-[#A5A6AC] px-2.5 sm:px-3 py-2 rounded-[9px] text-sm font-semibold hover:border-[#1DA8C6] hover:text-[#1DA8C6] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <FiDownload className="w-[13px] h-[13px]" />
                            <span className="hidden sm:inline">Before</span>
                        </button>
                    )}
                    {onDownloadAfter && (
                        <button
                            type="button"
                            onClick={onDownloadAfter}
                            disabled={!displayAfterUrl}
                            title="Download selected after image"
                            className="flex items-center gap-1.5 bg-[rgba(29,168,198,0.14)] border border-[rgba(29,168,198,0.25)] text-[#1DA8C6] px-2.5 sm:px-3 py-2 rounded-[9px] text-sm font-semibold hover:bg-[rgba(29,168,198,0.24)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <FiDownload className="w-[13px] h-[13px]" />
                            <span className="hidden sm:inline">After</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={openFullscreen}
                        disabled={!preview}
                        className="flex items-center gap-1.5 bg-[rgba(29,168,198,0.14)] text-[#1DA8C6] px-2.5 sm:px-3 py-2 rounded-[9px] text-sm font-semibold hover:bg-[rgba(29,168,198,0.24)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        </svg>
                        <span className="hidden sm:inline">Fullscreen</span>
                    </button>
                </div>
            </div>

            {viewMode === "split" ? (
                <div className="flex flex-col sm:flex-row w-full overflow-hidden bg-[#0E0F11] border-y border-white/[0.07] h-[min(70vh,640px)] sm:h-[min(52vh,480px)]">
                    {!preview ? (
                        <div className="flex-1 flex items-center justify-center p-6 text-center">
                            <div>
                                <p className="text-sm font-medium text-[#A5A6AC]">
                                    Upload a photo to begin
                                </p>
                                <p className="text-sm text-[#6B6C72] mt-1">
                                    Your patient photo will appear here
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <SmileZoomPane
                                src={preview}
                                label="ORIGINAL"
                                focus={beforeFocus}
                                view={beforeViewState}
                                onViewChange={(next) => applyView("before", next)}
                            />
                            <div className="h-px sm:h-auto sm:w-px bg-white/[0.07] shrink-0" />
                            <SmileZoomPane
                                src={displayAfterUrl}
                                label="GENERATED"
                                focus={afterFocus}
                                view={afterViewState}
                                onViewChange={(next) => applyView("after", next)}
                                emptyText="Ready for Enhancement"
                                accent
                            />
                        </>
                    )}
                </div>
            ) : (
                <div
                    className={`relative mx-auto w-full overflow-hidden bg-[#0E0F11] border-y border-white/[0.07] select-none ${
                        activeImageSrc ? "h-auto" : "aspect-[4/3] max-h-[220px] sm:max-h-[260px]"
                    }`}
                    style={
                        activeImageSrc
                            ? {
                                  aspectRatio: imageAspectRatio ?? 3 / 4,
                                  maxHeight: "min(58vh, 520px)",
                              }
                            : undefined
                    }
                >
                    {!preview ? (
                        <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                            <div>
                                <p className="text-sm font-medium text-[#A5A6AC]">
                                    Upload a photo to begin
                                </p>
                                <p className="text-sm text-[#6B6C72] mt-1">
                                    Your patient photo will appear here
                                </p>
                            </div>
                        </div>
                    ) : !displayAfterUrl ? (
                        <>
                            <img src={preview} alt="Before" className={imageClass} draggable={false} />
                            <span className="absolute top-3.5 left-3.5 z-[5] bg-[rgba(10,11,13,0.75)] backdrop-blur-sm px-[11px] py-[5px] rounded-full text-xs font-semibold tracking-wide border border-white/[0.07]">
                                BEFORE
                            </span>
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="bg-[rgba(10,11,13,0.85)] backdrop-blur-md border border-white/[0.07] px-6 py-4 rounded-xl text-center">
                                    <p className="text-sm font-semibold">Ready for Enhancement</p>
                                    <p className="text-sm text-[#6B6C72] mt-1">
                                        Generated smile design will appear here
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <img src={preview} alt="Before" className={imageClass} draggable={false} />
                            <img
                                src={displayAfterUrl}
                                alt="After overlay"
                                className={imageClass}
                                draggable={false}
                                style={{ opacity: overlayOpacity / 100 }}
                            />
                            <span className="absolute top-3.5 left-3.5 z-[5] bg-[rgba(10,11,13,0.75)] backdrop-blur-sm px-[11px] py-[5px] rounded-full text-xs font-semibold tracking-wide border border-white/[0.07] pointer-events-none">
                                ORIGINAL
                            </span>
                            <span className="absolute top-3.5 right-3.5 z-[5] bg-[rgba(10,11,13,0.75)] backdrop-blur-sm px-[11px] py-[5px] rounded-full text-xs font-semibold tracking-wide border border-[rgba(29,168,198,0.35)] text-[#1DA8C6] pointer-events-none">
                                DESIGNED {overlayOpacity}%
                            </span>
                        </>
                    )}
                </div>
            )}

            {viewMode === "overlay" && displayAfterUrl && (
                <div className="px-3 sm:px-5 py-3 border-b border-white/[0.07]">
                    <div className="flex items-center justify-between gap-3 text-xs font-semibold text-[#A5A6AC] mb-1.5">
                        <label htmlFor="live-overlay-opacity">Designed opacity</label>
                        <span className="text-[#1DA8C6] tabular-nums">{overlayOpacity}%</span>
                    </div>
                    <input
                        id="live-overlay-opacity"
                        type="range"
                        min={0}
                        max={100}
                        value={overlayOpacity}
                        onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                        className="w-full accent-[#1DA8C6]"
                    />
                </div>
            )}
        </div>
    );
}
